/**
 * Priority API client
 *
 * Provides methods for interacting with the Priority Engine API.
 */

// Priority Engine API base URL
const PRIORITY_API_BASE = import.meta.env.VITE_PRIORITY_API_BASE ?? 
  'https://us-west1-assistant-481119.cloudfunctions.net';

/**
 * Priority event in the history
 */
export interface PriorityEvent {
  id: string;
  timestamp: string;
  event_type: string;
  previous_score: number;
  new_score: number;
  score_delta: number;
  reason: string | null;
  rule_name: string | null;
}

/**
 * Priority score components breakdown
 */
export interface PriorityComponents {
  base: number;
  urgency: number;
  importance: number;
  values: number;
  context: number;
  decay: number;
  pinned: number;
  total: number;
}

/**
 * Recalculate response
 */
export interface RecalculateResponse {
  success: boolean;
  task_id: string;
  previous_score: number;
  new_score: number;
  components: PriorityComponents;
  reasons: string[];
  rules_applied: string[];
}

/**
 * Touch response
 */
export interface TouchResponse {
  success: boolean;
  task_id: string;
  current_score: number;
  is_pinned: boolean;
  needs_review: boolean;
}

/**
 * Pin response
 */
export interface PinResponse {
  success: boolean;
  task_id: string;
  is_pinned: boolean;
  pin_reason: string | null;
  pin_expires_at: string | null;
}

/**
 * Priority history response
 */
export interface PriorityHistoryResponse {
  success: boolean;
  task_id: string;
  events: PriorityEvent[];
}

/**
 * Daily batch response
 */
export interface DailyBatchResponse {
  success: boolean;
  batch_id: string;
  tasks_processed: number;
  tasks_updated: number;
  tasks_pinned: number;
  tasks_flagged_for_review: number;
  errors: string[];
  duration_seconds: number;
}

/**
 * Health check response
 */
export interface HealthCheckResponse {
  status: string;
  service: string;
  timestamp: string;
}

/**
 * Fetch helper for priority API (doesn't use auth - public endpoints)
 */
async function fetchPriorityApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${PRIORITY_API_BASE}${endpoint}`;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Check health of priority engine
 */
export async function healthCheck(): Promise<HealthCheckResponse> {
  return fetchPriorityApi<HealthCheckResponse>('/health-check');
}

/**
 * Recalculate priority for a task
 */
export async function recalculatePriority(taskId: string): Promise<RecalculateResponse> {
  return fetchPriorityApi<RecalculateResponse>('/priority-recalculate', {
    method: 'POST',
    body: JSON.stringify({ task_id: taskId }),
  });
}

/**
 * Touch a task during review (reset decay clock)
 * @param taskId - The task ID
 * @param bumpAmount - Optional amount to bump (+10 to +20 for bump, -10 to -20 for demote)
 */
export async function touchTask(
  taskId: string,
  bumpAmount?: number
): Promise<TouchResponse> {
  return fetchPriorityApi<TouchResponse>('/priority-touch', {
    method: 'POST',
    body: JSON.stringify({ 
      task_id: taskId,
      bump_amount: bumpAmount,
    }),
  });
}

/**
 * Pin a task to resist decay
 */
export async function pinTask(
  taskId: string,
  reason: string,
  days?: number
): Promise<PinResponse> {
  return fetchPriorityApi<PinResponse>('/priority-pin', {
    method: 'POST',
    body: JSON.stringify({
      task_id: taskId,
      pin: true,
      reason,
      days,
    }),
  });
}

/**
 * Unpin a task
 */
export async function unpinTask(taskId: string): Promise<PinResponse> {
  return fetchPriorityApi<PinResponse>('/priority-pin', {
    method: 'POST',
    body: JSON.stringify({
      task_id: taskId,
      pin: false,
    }),
  });
}

/**
 * Get priority history for a task
 */
export async function getPriorityHistory(
  taskId: string,
  limit?: number
): Promise<PriorityHistoryResponse> {
  const params = new URLSearchParams({ task_id: taskId });
  if (limit) params.append('limit', limit.toString());
  
  return fetchPriorityApi<PriorityHistoryResponse>(`/priority-history?${params}`);
}

/**
 * Trigger daily batch processing (admin use)
 */
export async function triggerDailyBatch(
  individualId?: string
): Promise<DailyBatchResponse> {
  const params = individualId ? `?individual_id=${individualId}` : '';
  return fetchPriorityApi<DailyBatchResponse>(`/priority-daily-batch${params}`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

